<template>
  <!--
    L.O.T.S. (FT-1472 rev 6): THE tarot-style Lot card — a LIVE template, not a
    baked art-gen image. The gold-bordered black card IS the base image
    (Tarot_Blank); the two ability BANDS, the name band and the art slot are laid
    on TOP of it as elements. The CONTENT is data:

      · name           <- role.name          (gold display caps)
      · light ability  <- role.ability        (top band — "moonlight")
      · shadow ability <- role.shadowAbility  (bottom band — "deep dusk")
      · art            <- the `art` prop / "art" slot (per-Lot illustration;
                          renders full-bleed on the card background; the bare
                          base card shows through when there's none)

    rev6 layout: the two ability areas are FULL-WIDTH gradient bands that hug the
    gold frame — a luminous moonlight band across the top (a single BOTTOM border
    facing inward), a deep-dusk band across the bottom (a single TOP border facing
    inward), the name + art the axis between them. No sun/eclipse icons.

    NAME ANCHORING: the name is the header of the CENTRE block (name + art),
    NOT of the light band. The light/shadow bands own the top/bottom zones and
    grow with their text toward the centre; the centre gives up ART height, so
    the name stays pinned to the top of the (stable) art region and never drifts
    when an ability runs long.

    Stable className hooks: .lot-card, .lot-card-light, .lot-card-name,
    .lot-card-art, .lot-card-shadow.
  -->
  <div
    class="lot-card"
    :style="{ width: width + 'px', backgroundImage: `url(${base})` }"
  >
    <!-- ART — full-bleed directly on the card background, BEHIND all the
         chrome (name, ability bands) — not a bordered inset in the middle. No
         art => nothing renders here and the bare tarot base card texture shows
         through. -->
    <div class="lot-card-art-bleed" v-if="art" aria-hidden="true">
      <slot name="art">
        <img :src="art" alt="" />
      </slot>
    </div>

    <div class="lot-card-inner">
      <!-- ============ LIGHT (top band) — the MOONLIGHT band ============ -->
      <!-- Full-width, a single bottom border facing inward toward the art. The
           luminous duotone fill is what a player reads at a glance as "the light
           side". -->
      <section class="lot-card-light">
        <p class="lot-card-row-text">{{ lightText }}</p>
      </section>

      <!-- ================ NAME + ART (the stable axis) ================ -->
      <section class="lot-card-center">
        <!-- the name is the header of the ART block, so it stays pinned to the
             top of the art no matter how long an ability grows -->
        <h2 class="lot-card-name">{{ name }}</h2>
        <div class="lot-card-subtitle" v-if="subtitle">{{ subtitle }}</div>

        <!-- pure flex spacer — reserves the centre's height so the name stays
             pinned to the top and gives it up as abilities grow; the actual
             art renders full-bleed on the card background, see above. -->
        <div class="lot-card-art"></div>
      </section>

      <!-- ============ SHADOW (bottom band) — the DEEP DUSK band ============ -->
      <!-- Full-width, a single top border facing inward toward the art. The
           plum-into-indigo fade reads as "the shadow side". -->
      <section class="lot-card-shadow">
        <p class="lot-card-row-text">{{ shadowText }}</p>
      </section>
    </div>
  </div>
</template>

<script>
import base from "../assets/frames/tarot-blank.png";

export default {
  name: "LotCard",
  props: {
    /** Anything Lot-shaped: needs `name`, `ability`, `shadowAbility`. */
    role: { type: Object, required: true },
    /**
     * The per-Lot illustration, as an image src. Renders full-bleed on the
     * card background, behind the chrome. A caller may instead fill the `art`
     * named slot. Left empty, no art layer renders — the bare base card shows.
     */
    art: { type: String, default: "" },
    /**
     * The line under the name — "XII · THE TRAITOR" (roman numeral + epithet).
     * NOT in the Lot model yet: pass it in for now (proposed `numeral`/`epithet`
     * optional Lot fields).
     */
    subtitle: { type: String, default: "" },
    /** Card width in px; height follows the base card's portrait aspect. */
    width: { type: Number, default: 384 },
  },
  data() {
    return { base };
  },
  computed: {
    // Every visible string comes from data, not the template.
    name() {
      return (this.role.name || "").toUpperCase();
    },
    lightText() {
      return this.role.ability || "";
    },
    shadowText() {
      return this.role.shadowAbility || "";
    },
  },
};
</script>

<style scoped lang="scss">
// The card ink: a single gold for hairlines + labels, a bone for the name.
$gold: #c8a24c;
$gold-soft: rgba(200, 162, 76, 0.55);
$gold-faint: rgba(200, 162, 76, 0.3);
$bone: #efe8da;

// The display pair (loaded as webfonts in public/index.html). Real fallbacks.
$display: "Cinzel", "Trajan Pro", "Cormorant Garamond", "Hoefler Text", Georgia,
  serif;
$body: "EB Garamond", "Cormorant Garamond", "Hoefler Text", Georgia,
  "Times New Roman", serif;

.lot-card {
  position: relative;
  // the base card image IS the frame; preserve its portrait aspect.
  aspect-ratio: 1032 / 1837;
  background-size: 100% 100%;
  background-repeat: no-repeat;
  color: $bone;
  font-family: $body;
  isolation: isolate;
}

// The content column. The two bands hug the gold frame: the LIGHT band's top
// tucks just below the top gold hairline, the SHADOW band's bottom just inside
// the bottom gold hairline, and — rev6 — the sides run FULL width to just
// inside the gold frame (no side inset). The centre (name + art) takes all the
// room between the bands.
.lot-card-inner {
  position: absolute;
  inset: 3.8% 6.6%;
  z-index: 2;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

// ---- LIGHT / SHADOW bands (own the top/bottom; grow toward the centre) ----
// Full-width gradient bands. They read as opposites AT A GLANCE by fill alone:
// the top band is a luminous moonlight duotone, the bottom band a deep-dusk
// fade. PADDING is tunable (FT-1474): `--lot-ability-padding` drives the
// vertical pad, horizontal is +6px so the text keeps clear of the frame.
.lot-card-light,
.lot-card-shadow {
  flex: 0 0 auto;
  text-align: center;
  padding: var(--lot-ability-padding, 15px)
    calc(var(--lot-ability-padding, 15px) + 6px);
}

// LIGHT band — a MOONLIGHT duotone: a pale warm-ivory blended into a cool cyan,
// luminous on the dark card and radiating via an outer bloom. rev6: SINGLE
// bottom border only (facing inward toward the art); no side/top borders, no
// neumorphic bevel (a single-border band would fight inset top/bottom edges).
// `--lot-light-color` (default cyan #aedce8, the chosen Scheme B) drives the
// cyan end + the border; `--lot-light-bg-opacity` / `--lot-light-border-
// opacity` still scale fill + border (FT-1474). `--lot-light-glow` overrides
// the shipped bloom.
.lot-card-light {
  border: 0;
  border-bottom: 1px solid
    color-mix(
      in srgb,
      var(--lot-light-color, #aedce8)
        calc(15% * var(--lot-light-border-opacity, 1)),
      transparent
    );
  background: linear-gradient(
    100deg,
    color-mix(
      in srgb,
      #fbf3e2 calc(3% * var(--lot-light-bg-opacity, 1)),
      transparent
    ),
    color-mix(
        in srgb,
        var(--lot-light-color, #aedce8)
          calc(3% * var(--lot-light-bg-opacity, 1)),
        transparent
      )
      58%,
    color-mix(
      in srgb,
      #cbeef6 calc(3% * var(--lot-light-bg-opacity, 1)),
      transparent
    )
  );
  // rev9: dropped another notch — the hue is now a GHOST, just barely readable.
  // The fills fell to ~3% and the border hairline to ~half its rev8 alpha; the
  // inner glow + outer bloom are toned to match, so the band reads as the
  // faintest breath of warm-cyan light rather than a whisper. A palette /
  // light-hue can still override the bloom via `--lot-light-glow`.
  box-shadow:
    inset 0 0 18px
      color-mix(in srgb, var(--lot-light-color, #aedce8) 2%, transparent),
    var(
      --lot-light-glow,
      0 0 12px rgba(174, 220, 232, 0.04),
      0 0 5px rgba(200, 236, 244, 0.035)
    );
}

// SHADOW band — DEEP DUSK: a muted plum-violet at the inner edge fading down
// into an indigo-black. rev6: SINGLE top border only (facing inward toward the
// art); no side/bottom borders, no neumorphism. `--lot-shadow-color` (default
// violet #8e74c0) drives the plum end; `--lot-shadow-border-color` (#a38ed0)
// the top hairline; `--lot-shadow-bg-opacity` / `--lot-shadow-border-opacity`
// scale fill + border (FT-1474).
.lot-card-shadow {
  border: 0;
  border-top: 1px solid
    color-mix(
      in srgb,
      var(--lot-shadow-border-color, rgb(var(--reader-rgb)))
        calc(11% * var(--lot-shadow-border-opacity, 1)),
      transparent
    );
  // rev9: still reads SUNKEN — the recess depth (the dark top lip + the inset
  // shadow) is UNCHANGED. Only the violet TONE dropped another notch: the plum
  // whisper at the bottom inner edge is now ~3% and the top hairline ~half its
  // rev8 alpha, so the hue is a ghost while the pressed-in depth stays.
  background: linear-gradient(
    180deg,
    color-mix(
      in srgb,
      #06040c calc(94% * var(--lot-shadow-bg-opacity, 1)),
      transparent
    ),
    color-mix(
        in srgb,
        #120c22 calc(80% * var(--lot-shadow-bg-opacity, 1)),
        transparent
      )
      55%,
    color-mix(
      in srgb,
      var(--lot-shadow-color, rgb(var(--reader-rgb)))
        calc(3% * var(--lot-shadow-bg-opacity, 1)),
      transparent
    )
  );
  // The depth cue: a strong soft inset shadow at the TOP inner lip casting
  // downward into the recess, fading to a faint light catch at the bottom inner
  // edge. This is what makes the band read pressed INTO the card. (Unchanged in
  // rev8 — the sunken read must survive the fainter tone.)
  box-shadow:
    inset 0 10px 15px -6px rgba(0, 0, 0, 0.78),
    inset 0 -2px 3px color-mix(in srgb, var(--lot-shadow-border-color, rgb(var(--reader-rgb)))
          5%, transparent);
}

// ABILITY TEXT — near-white, high contrast on both gradients (rev6), with a
// soft dark shadow so it reads over the lighter reaches of each band. SIZE is
// tunable (FT-1474); fallback reproduces the shipped 16px.
.lot-card-row-text {
  margin: 0;
  font-size: var(--lot-ability-font-size, 16px);
  line-height: 1.34;
}
.lot-card-light .lot-card-row-text {
  color: #f4f8fc;
  text-shadow: 0 1px 6px rgba(0, 0, 0, 0.55);
}
.lot-card-shadow .lot-card-row-text {
  color: #ece8f6;
  text-shadow: 0 1px 6px rgba(0, 0, 0, 0.7);
}

// ---- NAME + ART (the stable axis) ----
.lot-card-center {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding: 14px 0;
}
// NAME is tunable (FT-1474): `--lot-name-size` is the font size; `--lot-name-y`
// (a unitless px value) slides the name DOWN via transform — so it can be
// settled anywhere from just under the light band down through the art WITHOUT
// reflowing the subtitle/art beneath it. Fallbacks: 32px, no offset.
.lot-card-name {
  flex: 0 0 auto; // fixed header of the art block — never drifts
  margin: 0;
  font-family: $display;
  font-weight: 600;
  font-size: var(--lot-name-size, 32px);
  letter-spacing: 0.06em;
  line-height: 1.04;
  text-align: center;
  // GOLD — matches the frame gold ($gold), the Reader accent of the chosen
  // Scheme B. `--lot-name-color` lets a palette recolour it; the fallback is
  // the shipped gold, so the card is unchanged with no lab.
  color: var(--lot-name-color, #{$gold});
  text-shadow: 0 2px 12px rgba(0, 0, 0, 0.8);
  transform: translateY(calc(var(--lot-name-y, 0) * 1px));
}
.lot-card-subtitle {
  flex: 0 0 auto;
  margin-top: 7px;
  font-family: $display;
  font-size: 12px;
  letter-spacing: 0.3em;
  padding-left: 0.3em;
  text-align: center;
  color: $gold;
  opacity: 0.9;
}
// PURE FLEX SPACER: no longer holds the art itself — it just reserves the
// centre's height so the name stays pinned to the top and gives that height up
// as abilities grow. The actual art is `.lot-card-art-bleed` below, a full-card
// layer, so this element carries no visual style of its own.
.lot-card-art {
  flex: 1 1 auto;
  min-height: 40px;
  margin-top: 12px;
}

// ART, FULL-BLEED. Sits directly on the card's base texture, edge to edge,
// BEHIND every chrome element (inner z-index 2). No art => this element doesn't
// render at all (see the template's `v-if="art"`) and the bare base card shows
// through.
.lot-card-art-bleed {
  position: absolute;
  inset: 0;
  z-index: 1;
  overflow: hidden;
  // FT-1546: cut to the base card's own rounded corners (its PNG's radius, as
  // a share of its width / height), so real art never squares them off
  border-radius: 4.4% / 2.5%;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    display: block;
  }
}
</style>
